"use client";

import LocaleLink from "@/component/layout/LocaleLink";
import { useContent } from "@/hooks/useContent";
import type { Locale } from "@/types/home";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLocale } from "next-intl";
import type { InvestContent } from "./asset";

/** Asset-class opportunity cards (panel-managed) — each links to its detail page. */
export default function InvestAssets() {
  const locale = useLocale() as Locale;
  const ar = locale === "ar";
  const { data } = useContent<InvestContent>("investment");
  const assets = data?.assets ?? [];

  if (assets.length === 0) return null;

  return (
    <section className="mx-auto max-w-360 px-6 py-16 md:py-24 lg:px-10">
      <span className="text-[11px] uppercase tracking-widest text-accent">{ar ? "فئات الأصول" : "Asset classes"}</span>
      <div className="mt-10 grid gap-6 md:grid-cols-2">
        {assets.map((a, i) => (
          <motion.div
            key={a.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
          >
            <LocaleLink
              href={`/investment/${a.id}`}
              className="group block border border-black/10 transition-colors hover:border-accent dark:border-white/10"
            >
              <div className="relative h-48 overflow-hidden bg-black/5 dark:bg-white/5">
                {a.image && <img src={a.image} alt={a.title[locale]} className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:scale-105 group-hover:grayscale-0" />}
                <span className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center bg-white/90 text-ash opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:bg-ash/90 dark:text-white">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-bebas text-2xl uppercase text-ash transition-colors group-hover:text-accent dark:text-white">{a.title[locale]}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ash/60 dark:text-white/60">{a.thesis[locale]}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest text-accent">
                  {ar ? "اعرف المزيد" : "Learn more"}
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 rtl:rotate-[-90deg]" />
                </span>
              </div>
            </LocaleLink>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
